import { useQuery } from "@tanstack/react-query"
import { CircleAlert, FileDown, Lock } from "lucide-react"
import { useRef, useState } from "react"
import { useParams } from "react-router"

import { api } from "@/api/cliente"
import { LogoSetieca } from "@/componentes/marca/LogoSetieca"
import { pdfDeLosExpedientes } from "@/componentes/reportes/pdfExpedientes"
import { descargar, logoComoPng } from "@/componentes/reportes/pdfReporte"
import type { Expedientes } from "@/componentes/reportes/tiposExpedientes"
import { comoFecha, comoFechaHora } from "@/lib/fechas"

/**
 * Los expedientes abiertos del 2.0 por estatus, desde el enlace del parte de las 7:00 o
 * de las 19:00 (`/e/<token>`, 13/10). **Sin sesión, sin menú y sin poder tocar nada**,
 * con el aspecto del reporte de cuentas por pagar: membrete, total arriba y un bloque por
 * estatus, que en el teléfono se apila en tarjetas.
 *
 * ⚠ **Es la FOTO de ese parte**, no el estado de ahora: los cambios del día llegan uno a
 * uno al tema «Actualización de Expedientes».
 */

export function ExpedientesPublica() {
  const { token = "" } = useParams()
  const marca = useRef<HTMLDivElement>(null)
  const [generando, setGenerando] = useState(false)
  const [fallo, setFallo] = useState(false)
  const consulta = useQuery({
    queryKey: ["publico", "expedientes", token],
    queryFn: () => api.get<Expedientes>(`/api/v1/publico/expedientes/${token}`),
    retry: false,
  })

  if (consulta.isPending) {
    return (
      <p role="status" className="p-10 text-center text-sm" style={{ color: "var(--fg-muted)" }}>
        Cargando…
      </p>
    )
  }
  if (consulta.isError || !consulta.data) {
    return (
      <div className="mx-auto max-w-lg p-10 text-center">
        <CircleAlert size={28} aria-hidden={true} style={{ color: "var(--fg-muted)" }} />
        <h1 className="mt-3 text-lg font-semibold">Este enlace ya no vale</h1>
        <p className="mt-2 text-sm" style={{ color: "var(--fg-muted)" }}>
          Puede haber caducado. El del próximo parte trae el reporte nuevo.
        </p>
      </div>
    )
  }

  const e = consulta.data
  const tenue = { color: "var(--fg-muted)" }
  const borde = { borderColor: "var(--border)" }

  async function exportar() {
    setGenerando(true)
    setFallo(false)
    try {
      const logo = await logoComoPng(
        marca.current?.querySelector("svg") ?? null,
        "rgb(122, 27, 56)",
      )
      descargar(
        await pdfDeLosExpedientes(e, logo),
        `Expedientes abiertos ${comoFecha(e.fecha).replaceAll("/", "-")}.pdf`,
      )
    } catch {
      setFallo(true)
    } finally {
      setGenerando(false)
    }
  }

  return (
    <div className="hoja-reporte mx-auto max-w-6xl p-4 sm:p-8">
      <div className="no-print mb-4 flex flex-wrap items-center justify-between gap-3">
        <p
          className="flex items-center gap-2 rounded-[var(--radius-control)] border border-dashed px-3 py-2 text-xs"
          style={{ ...borde, ...tenue }}
        >
          <Lock size={12} aria-hidden={true} />
          Vista de sólo lectura. El enlace caduca el {comoFecha(e.expira_en)}.
        </p>
        <button
          type="button"
          onClick={exportar}
          disabled={generando}
          className="foco-cristal inline-flex min-h-11 items-center gap-2 rounded-[var(--radius-control)] border px-4 text-sm font-medium disabled:opacity-60"
          style={borde}
        >
          <FileDown size={16} aria-hidden={true} />
          {generando ? "Generando…" : "Exportar PDF"}
        </button>
        {fallo && (
          <p role="alert" className="w-full text-xs" style={{ color: "var(--destructive)" }}>
            No se pudo generar el PDF. Vuelve a intentarlo.
          </p>
        )}
      </div>

      <header className="mb-5 flex items-center justify-between gap-4 border-b pb-3" style={borde}>
        <div ref={marca} className="shrink-0" style={{ color: "var(--fg)" }}>
          <LogoSetieca titulo={e.empresa_nombre} className="h-12 w-auto sm:h-14" />
        </div>
        <div className="min-w-0 text-right">
          <p className="text-sm font-semibold uppercase sm:text-base">{e.empresa_nombre}</p>
          {e.empresa_rif ? (
            <p className="text-xs" style={tenue}>
              RIF {e.empresa_rif}
            </p>
          ) : null}
        </div>
      </header>

      <p className="text-xs" style={tenue}>
        Reporte del {comoFecha(e.fecha)}, generado el {comoFechaHora(e.generado_en)}
      </p>
      <h1 className="mt-1 text-xl font-semibold">{e.titulo}</h1>

      <dl className="mt-4 grid gap-3 text-sm sm:grid-cols-2 lg:grid-cols-4">
        <div className="rounded-[var(--radius-control)] border p-3" style={borde}>
          <dt style={tenue}>Expedientes abiertos</dt>
          <dd className="mt-1 text-base font-semibold tabular-nums">{e.total}</dd>
        </div>
        {e.grupos.map((g) => (
          <div key={g.estatus} className="rounded-[var(--radius-control)] border p-3" style={borde}>
            <dt style={tenue}>{g.estatus}</dt>
            <dd className="mt-1 text-base font-semibold tabular-nums">{g.expedientes.length}</dd>
          </div>
        ))}
      </dl>

      {e.grupos.length === 0 ? (
        <p className="mt-8 text-sm" style={tenue}>
          No había expedientes abiertos.
        </p>
      ) : (
        e.grupos.map((g) => (
          <section key={g.estatus} className="grupo-reporte mt-8">
            <div
              className="flex flex-wrap items-baseline justify-between gap-2 border-b pb-1"
              style={borde}
            >
              <h2 className="text-base font-semibold">{g.estatus}</h2>
              <p className="text-sm font-semibold tabular-nums">{g.expedientes.length}</p>
            </div>
            <div className="overflow-x-auto">
              <table className="tabla-apilable mt-2 w-full text-sm">
                <thead>
                  <tr className="text-left text-xs" style={tenue}>
                    <th className="py-1 pr-3 font-medium">Expediente</th>
                    <th className="py-1 pr-3 font-medium">Cliente</th>
                    <th className="py-1 font-medium">ETA</th>
                  </tr>
                </thead>
                <tbody>
                  {g.expedientes.map((x) => (
                    <tr key={x.numero} className="border-t align-top" style={borde}>
                      <td data-rotulo="Expediente" className="py-1 pr-3 font-medium tabular-nums">
                        {x.numero}
                      </td>
                      <td data-rotulo="Cliente" className="py-1 pr-3">
                        {x.cliente}
                      </td>
                      <td data-rotulo="ETA" className="whitespace-nowrap py-1 tabular-nums">
                        {comoFecha(x.eta) || "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        ))
      )}
    </div>
  )
}
